import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  Button,
  LinearProgress,
} from "@mui/material";
import { useTestMode } from "context/TestModeContext";
import { adminTheme } from "theme/adminTheme";
import {
  mockProcesses,
  mockProcessExecutions,
  mockEmails,
  mockAccounts,
  mockBankAccounts,
} from "data/mockData";
import { createApi } from "util/ApiCreator";
import { ProcessController } from "api/main/ProcessController";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import { ProcessResponse, ProcessExecutionResponse } from "api/main/data-contracts";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EmailIcon from "@mui/icons-material/Email";
import GroupIcon from "@mui/icons-material/Group";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";

interface InfoCardProps {
  title: string;
  icon: React.ReactNode;
  items: { label: string; value: string | number; color?: string }[];
}

function InfoCard({ title, icon, items }: InfoCardProps) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: adminTheme.borderRadius.lg,
        border: `1px solid ${adminTheme.colors.border}`,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${adminTheme.colors.border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: adminTheme.borderRadius.sm,
            bgcolor: "rgba(99, 102, 241, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: adminTheme.colors.primary,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              borderBottom:
                index < items.length - 1
                  ? `1px solid ${adminTheme.colors.borderLight}`
                  : "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              {item.label}
            </Typography>
            <Typography
              variant="body"
              sx={{
                fontWeight: 600,
                color: item.color || adminTheme.colors.textPrimary,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default function DashboardInfo() {
  const navigate = useNavigate();
  const { isTestMode } = useTestMode();
  const processApi = useRef(createApi("process") as ProcessController);
  const executionApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);

  const [processes, setProcesses] = useState<ProcessResponse[]>([]);
  const [executions, setExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isTestMode) {
      setProcesses(mockProcesses);
      setExecutions(mockProcessExecutions);
      setLoading(false);
    } else {
      fetchData();
    }
  }, [isTestMode]);

  async function fetchData() {
    setLoading(true);
    try {
      const [processData, executionData] = await Promise.all([
        processApi.current.findAll(),
        executionApi.current.findAll(),
      ]);
      setProcesses(processData || []);
      setExecutions(executionData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  // Calculate stats
  const stats = {
    processes: {
      total: processes.length,
      active: processes.filter((p) => p.active).length,
      inactive: processes.filter((p) => !p.active).length,
    },
    executions: {
      total: executions.length,
      inProgress: executions.filter((e) => e.status === "IN_PROGRESS").length,
      finished: executions.filter((e) => e.status === "FINISHED").length,
      cancelled: executions.filter((e) => e.status === "CANCELLED").length,
    },
    emails: isTestMode
      ? {
          total: mockEmails.length,
          sent: mockEmails.filter((e) => e.status === "SUCCEED").length,
          failed: mockEmails.filter((e) => e.status === "FAILED").length,
        }
      : { total: 0, sent: 0, failed: 0 },
    accounts: isTestMode
      ? {
          total: mockAccounts.length,
          admins: mockAccounts.filter((a) => a.roles?.includes("ADMIN")).length,
          active: mockAccounts.filter((a) => a.fullyCreated).length,
        }
      : { total: 0, admins: 0, active: 0 },
    bankAccounts: isTestMode
      ? {
          total: mockBankAccounts.length,
          approved: mockBankAccounts.filter((b) => b.approved).length,
        }
      : { total: 0, approved: 0 },
  };

  const successRate =
    stats.executions.total > 0
      ? Math.round(
          (stats.executions.finished / stats.executions.total) * 100
        )
      : 0;

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard")}
            sx={{ mb: 2, color: adminTheme.colors.textSecondary }}
          >
            Atgal i valdymo skydelio
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: adminTheme.borderRadius.md,
                background: adminTheme.gradients.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoOutlinedIcon sx={{ color: "white", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: adminTheme.colors.textPrimary,
                }}
              >
                Sistemos informacija
              </Typography>
              <Typography
                variant="body"
                sx={{ color: adminTheme.colors.textSecondary }}
              >
                Detali statistika ir sistemos busena
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Overall Stats */}
        <Card
          sx={{
            mb: 4,
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            background: adminTheme.gradients.dark,
            color: "white",
          }}
        >
          <Box sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Bendra apzvalga
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {stats.processes.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Viso procesu
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {stats.executions.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Viso vykdymu
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, mb: 1, color: "#34D399" }}
                  >
                    {successRate}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Sekmingumo rodiklis
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 700, mb: 1, color: "#FBBF24" }}
                  >
                    {stats.executions.inProgress}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Aktyvus vykdymai
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Detailed Stats Grid */}
        <Grid container spacing={3}>
          {/* Processes */}
          <Grid item xs={12} md={6} lg={4}>
            <InfoCard
              title="Procesai"
              icon={<AccountTreeIcon />}
              items={[
                { label: "Viso procesu", value: stats.processes.total },
                {
                  label: "Aktyvus",
                  value: stats.processes.active,
                  color: adminTheme.colors.success,
                },
                {
                  label: "Neaktyvus",
                  value: stats.processes.inactive,
                  color: adminTheme.colors.textMuted,
                },
                {
                  label: "Aktyvumo procentas",
                  value: stats.processes.total > 0
                    ? `${Math.round((stats.processes.active / stats.processes.total) * 100)}%`
                    : "0%",
                },
              ]}
            />
          </Grid>

          {/* Executions */}
          <Grid item xs={12} md={6} lg={4}>
            <InfoCard
              title="Vykdymai"
              icon={<PlayArrowIcon />}
              items={[
                { label: "Viso vykdymu", value: stats.executions.total },
                {
                  label: "Vykdomi",
                  value: stats.executions.inProgress,
                  color: adminTheme.colors.primary,
                },
                {
                  label: "Uzbaigti",
                  value: stats.executions.finished,
                  color: adminTheme.colors.success,
                },
                {
                  label: "Atsaukti",
                  value: stats.executions.cancelled,
                  color: adminTheme.colors.error,
                },
              ]}
            />
          </Grid>

          {/* Emails */}
          <Grid item xs={12} md={6} lg={4}>
            <InfoCard
              title="El. laiskai"
              icon={<EmailIcon />}
              items={[
                { label: "Viso laisku", value: stats.emails.total },
                {
                  label: "Issiusti sekmingai",
                  value: stats.emails.sent,
                  color: adminTheme.colors.success,
                },
                {
                  label: "Nepavyke issiusti",
                  value: stats.emails.failed,
                  color: adminTheme.colors.error,
                },
                {
                  label: "Siuntimo sekmingumas",
                  value: stats.emails.total > 0
                    ? `${Math.round((stats.emails.sent / stats.emails.total) * 100)}%`
                    : "0%",
                },
              ]}
            />
          </Grid>

          {/* Accounts */}
          <Grid item xs={12} md={6} lg={4}>
            <InfoCard
              title="Paskyros"
              icon={<GroupIcon />}
              items={[
                { label: "Viso paskyru", value: stats.accounts.total },
                {
                  label: "Administratoriai",
                  value: stats.accounts.admins,
                  color: adminTheme.colors.primary,
                },
                {
                  label: "Aktyvios paskyros",
                  value: stats.accounts.active,
                  color: adminTheme.colors.success,
                },
              ]}
            />
          </Grid>

          {/* Bank Accounts */}
          <Grid item xs={12} md={6} lg={4}>
            <InfoCard
              title="Banko saskaitos"
              icon={<AccountBalanceIcon />}
              items={[
                { label: "Viso saskaitu", value: stats.bankAccounts.total },
                {
                  label: "Patvirtintos",
                  value: stats.bankAccounts.approved,
                  color: adminTheme.colors.success,
                },
                {
                  label: "Laukiancios patvirtinimo",
                  value: stats.bankAccounts.total - stats.bankAccounts.approved,
                  color: adminTheme.colors.warning,
                },
              ]}
            />
          </Grid>

          {/* System Status */}
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${adminTheme.colors.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: adminTheme.borderRadius.sm,
                    bgcolor: "rgba(16, 185, 129, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: adminTheme.colors.success,
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Sistemos busena
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: adminTheme.colors.success }} />
                  <Typography>API veikia normaliai</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: adminTheme.colors.success }} />
                  <Typography>Duomenu baze prieinama</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: adminTheme.colors.success }} />
                  <Typography>El. pastu paslauga aktyvi</Typography>
                </Box>
                {isTestMode && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "rgba(245, 158, 11, 0.1)",
                      borderRadius: adminTheme.borderRadius.sm,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ScheduleIcon sx={{ color: adminTheme.colors.warning }} />
                    <Typography
                      variant="body2"
                      sx={{ color: adminTheme.colors.warning }}
                    >
                      Test mode aktyvus - rodomi demo duomenys
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
